import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import GoogleIcon from '@mui/icons-material/Google';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DevicesIcon from '@mui/icons-material/Devices';
import { signInWithEmail, signInWithGoogle, signUpWithEmail } from '../firebase/authService';
import { trackLogin, trackSignUp } from '../utils/analytics';

const BENEFITS = [
  { icon: <AutoStoriesIcon sx={{ fontSize: 18, color: '#FF6123' }} />, text: 'Access all HSK stories with a Premium subscription' },
  { icon: <VolumeUpIcon sx={{ fontSize: 18, color: '#FF6123' }} />, text: 'Native audio with word-by-word highlighting' },
  { icon: <DevicesIcon sx={{ fontSize: 18, color: '#FF6123' }} />, text: 'Track your progress and sync across all devices' },
];

export default function LoginModal({ open, onClose, showPremiumUpsell = false }) {
  const [isSignUp, setIsSignUp] = useState(false);

  // Default to sign-up tab when opened from a premium upgrade flow
  useEffect(() => {
    if (open) setIsSignUp(showPremiumUpsell);
  }, [open, showPremiumUpsell]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    let result;
    if (isSignUp) {
      result = await signUpWithEmail(email, password, name);
    } else {
      result = await signInWithEmail(email, password);
    }

    if (result.success) {
      if (isSignUp) { trackSignUp('email'); } else { trackLogin('email'); }
      setSuccess(true);
      setLoading(false);
      setTimeout(() => {
        onClose();
        setTimeout(() => {
          setSuccess(false);
          setEmail('');
          setPassword('');
          setName('');
        }, 300);
      }, 1000);
    } else {
      setLoading(false);
      setError(result.error);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess(false);
    setLoading(true);
    const result = await signInWithGoogle();

    if (result.redirecting) return;

    if (result.success) {
      trackLogin('google');
      setSuccess(true);
      setLoading(false);
      setTimeout(() => {
        onClose();
        setTimeout(() => { setSuccess(false); }, 300);
      }, 1000);
    } else {
      setLoading(false);
      setError(result.error);
    }
  };

  const handleSwitch = () => {
    setIsSignUp(v => !v);
    setError('');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: '#fff',
          borderRadius: 0,
          outline: 'none',
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #09148B 0%, #070f6b 100%)',
            px: 3,
            pt: 3,
            pb: 2.5,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.25 }}>
            <Box component="img" src={`${process.env.PUBLIC_URL || ''}/assets/logo.png`} alt="Dumplingo" sx={{ height: 28, width: 'auto', display: 'block', filter: 'brightness(0) invert(1)' }} />
          </Box>
          {isSignUp && (
            <Typography
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontStyle: 'italic',
                fontWeight: 700,
                fontSize: 18,
                color: 'rgba(255,255,255,0.92)',
                lineHeight: 1.3,
              }}
            >
              Create a free account to subscribe to premium
            </Typography>
          )}
          {isSignUp && (
            <Typography
              sx={{
                fontFamily: '"Raleway", sans-serif',
                fontSize: 13,
                color: 'rgba(255,255,255,0.78)',
                mt: 0.5,
              }}
            >
              Free account required · then subscribe to Dumplingo Premium
            </Typography>
          )}
        </Box>

        {/* Benefits (shown on sign-up or premium upsell) */}
        {(isSignUp || showPremiumUpsell) && (
          <Box
            sx={{
              px: 3,
              py: 2,
              background: '#fdf8f5',
              borderBottom: '1px solid #ede6e0',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 12.5, color: '#9e8e84', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.25 }}>
              With Dumplingo Premium
            </Typography>
            {BENEFITS.map((b, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'rgba(255,97,35,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {b.icon}
                </Box>
                <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 13, fontWeight: 600, color: '#3b2d23', lineHeight: 1.35 }}>
                  {b.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Form */}
        <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {isSignUp ? 'Account created successfully!' : 'Logged in successfully!'}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {isSignUp && (
              <TextField fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} required />
            )}
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} required />
            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mb: 2.5 }} required />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mb: 1.5,
                py: 1.5,
                background: '#FF6123',
                fontFamily: '"Raleway", sans-serif',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 16,
                borderRadius: 2,
                '&:hover': { background: '#E55520' },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : isSignUp ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<GoogleIcon />}
            sx={{
              mb: 2,
              py: 1.5,
              borderColor: 'rgba(0,0,0,0.2)',
              color: '#2c2017',
              fontFamily: '"Raleway", sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { borderColor: 'rgba(0,0,0,0.3)', background: 'rgba(0,0,0,0.02)' },
            }}
          >
            Continue with Google
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              component="span"
              sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 13.5, color: '#9e8e84' }}
            >
              {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
            </Typography>
            <Typography
              component="span"
              onClick={handleSwitch}
              sx={{
                fontFamily: '"Raleway", sans-serif',
                fontSize: 13.5,
                fontWeight: 700,
                color: '#FF6123',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {isSignUp ? 'Sign in' : 'Sign up, it\'s free'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}